import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { _ } from '_locales';
import { colors } from '_styles';
import images from '_images';
import { connect } from 'react-redux';
import eventTracker from '../../../helpers/eventTracker';

const kidsKitConfig = {
  // Circular Materials web app brand number
  6: {
    urls: {
      age3to5: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025-03-circular-materials/3-5.pdf',
      age6to8: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025-03-circular-materials/6-8.pdf',
      age9to12: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025-03-circular-materials/9-12.pdf'
    },
    description: 'resources.circular_materials_kids_kit_description'
  },
  default: {
    urls: {
      age3to5: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025_04/2025_04_3-5.pdf',
      age6to8: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025_04/2025_04_6-8.pdf',
      age9to12: 'https://cdn.recyclecoach.com/resources/kids-recycle-kit/2025_04/2025_04_9-12.pdf'
    },
    description: 'resources.get_your_kids_involved_food_waste'
  }
};

const RcForKids = ({
  windowsWidth,
  colors,
  webappConfig
}) => {
  const { main_color } = colors;
  const { app_download_branded_app } = webappConfig;
  const config = kidsKitConfig[app_download_branded_app] || kidsKitConfig.default;

  const EVENT_PREFIX = 'KIDS_KIT_PDF_DOWNLOAD__FEB_2025';

  const data = [
    {
      id: 1,
      name: 'resources.age_3_5',
      href: config.urls.age3to5,
      title: 'Ages 3-5',
      eventName: `${EVENT_PREFIX}_AGE_3_to_5`
    },
    {
      id: 2,
      name: 'resources.age_6_8',
      href: config.urls.age6to8,
      title: 'Ages 6-8',
      eventName: `${EVENT_PREFIX}_AGE_6_to_8`
    },
    {
      id: 3,
      name: 'resources.age_9_12',
      href: config.urls.age9to12,
      title: 'Ages 9-12',
      eventName: `${EVENT_PREFIX}_AGE_9_to_12`
    }
  ];

  return (
    <Container windowsWidth={windowsWidth}>
      <Title>
        {_('resources.recycling_for_kids')}
      </Title>
      <ContentHeader>
        <Image src={images.education.kids} alt="kids" />
        <Paragraph>
          {_(config.description)}
        </Paragraph>
      </ContentHeader>

      {/* buttons */}
      <AllButtonsContainer windowsWidth={windowsWidth}>
        {data.map((item) => (
          <ButtonContainer key={item.id} windowsWidth={windowsWidth}>
            <>
              <Text>{_('education.download_activity_package_for')}</Text>
              <Age windowsWidth={windowsWidth}>{_(item.name)}</Age>
            </>
            <DownloadButton
              mainColor={main_color || false}
              href={item.href}
              aria-label={`Download Recycle For Kids for ${_(item.name)}`}
              onClick={() => eventTracker(item.eventName, '')}
              target="_blank"
            >
              {_('education.download')}
            </DownloadButton>
          </ButtonContainer>
        ))}
      </AllButtonsContainer>
    </Container>
  );
};

export default connect(
  (store) => ({
    blogs: store.education.blogs,
    selectedLang: store.config.selectedLang,
    colors: store.config.templateColour ? JSON.parse(store.config.templateColour) : {},
    webappConfig: store.config.webappConfig,
  })
)(RcForKids);

const OpacityAnimation = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

const Container = styled.div`
  box-sizing: border-box;
  padding: 10px 0;
  padding-top: 5px;
  background-color: white;
  border-radius: 10px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 18px 0px;
  margin-top: 15px;
  overflow: hidden;
  animation: ${OpacityAnimation} 0.5s;
`;

const Title = styled.h2`
  display:flex;
  align-items: center;
  font-size: 18px !important;
  color: rgba(64, 64, 64, 1);
  font-weight: 700 !important;
  border-bottom: 2px solid #E7ECEF;
  padding-bottom: 10px !important;
  padding-left: 10px !important;
  margin-top: 10px !important;
  text-transform: capitalize;
`;

const Paragraph = styled.p`
  font-size: 16px !important;
  color: #404040 !important;
  padding: 10px 0 !important;
`;

const DownloadButton = styled.a`
  display: block;
  border-radius: 4px;
  padding: 9px 25px;
  font-weight: 500 !important;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 13px !important;
  margin: ${({ margin }) => margin};
  text-transform: capitalize;
  background-color: ${({ mainColor }) => (mainColor ? `#${mainColor}` : `${colors.LIGHT_BLUE}`)};
  color: white;
  box-shadow: hsla(0, 0%, 38.82352941176471%, 0.2) 0px 2px 8px 0px;
  transition: all .3s;
  text-decoration: none;
  letter-spacing: 0.3px;

  &:hover {
    border: ${({ mainColor }) => (mainColor ? `1px solid #${mainColor}` : `1px solid ${colors.LIGHT_BLUE}`)};
    background-color: #fff;
    color: ${({ mainColor }) => (mainColor ? `#${mainColor}` : `${colors.LIGHT_BLUE}`)};
    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 4px 0px;
    opacity: 0.9;
  }
`;

const AllButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: ${({ windowsWidth }) => (windowsWidth >= 650 ? 'row' : 'column')};
  text-align: center;
  padding: 0 8px;
`;

const Text = styled.p`
  font-size: 14px !important;
  font-weight: lighter !important;
  letter-spacing: 0.3px;
  margin: 0;
`;

const Age = styled.p`
  color: ${colors.LIGHT_BLUE};
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: ${({ windowsWidth }) => (windowsWidth >= 650 ? '12px 0 20px !important' : '5px !important')};
  letter-spacing: 0.3px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: ${({ windowsWidth }) => (windowsWidth >= 650 ? 'column' : 'row')};
  align-items: ${({ windowsWidth }) => (windowsWidth >= 650 ? 'stretch' : 'center')};
  justify-content: space-between;
  flex: 1;
  padding: 5px;
  margin-bottom: 10px;
`;

const Image = styled.img`
  height: 100px;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px;
  margin-bottom: 35px;
`;
